import React, { PropsWithChildren, useState } from "react";
import { Popup, Form, Dropdown, Label, Checkbox, Button } from "semantic-ui-react";
import { useNotifySubscriptionGroups } from "../../../../hooks/useNotifySubscriptionGroups";
import Tooltip from "../../../../../../components/Tooltip";
import { GetNotifyContactsResponse } from "../../../../../../queries/notify";
import { useNotifyContactsBulkAddToSubscriptionGroups } from "../../../../hooks/useNotifyContactsBulkAddToSubscriptionGroups";

export function AddNotifyContactsToSubscriptionGroupsPopup({
  children,
  notifyContactIds,
  notifyContactsById,
  onSubmit = () => {},
}: PropsWithChildren<{
  notifyContactIds: number[];
  notifyContactsById: Record<number, GetNotifyContactsResponse["data"][number]>;
  onSubmit?: () => void;
}>) {
  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups({});
  const [selectedSubscriptionGroupIds, setSelectedSubscriptionGroupIds] = useState<number[]>([]);
  const [reAddNonMembers, setReAddNonMembers] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const { bulkAddNotifyContactsToSubscriptionGroups, isAddNotifyContactsToSubscriptionGroupsLoading } =
    useNotifyContactsBulkAddToSubscriptionGroups();

  const notifyContactsNotInGroup = !reAddNonMembers
    ? notifyContactIds
        .map((id) => {
          const notifyContact = notifyContactsById[id];
          const groupsTheNotifyContactIsNotMemberOf =
            subscriptionGroups
              ?.filter((group: (typeof subscriptionGroups)[0]) => {
                // Check if the group is not "Public"
                if (group.visibility === "Public") {
                  return false;
                }

                // Check if no contact method has a preference for this group
                const hasPreference = notifyContact.contactMethods.some((contactMethod) =>
                  contactMethod.subscriptionPreferences.some((pref) => pref.subscriptionGroupId === group.id),
                );

                return !hasPreference;
              })
              .map((group: (typeof subscriptionGroups)[0]) => group.id) ?? [];
          return {
            notifyContact,
            groupsTheNotifyContactIsNotMemberOf,
          };
        })
        .filter((item) => item.groupsTheNotifyContactIsNotMemberOf.length > 0)
    : [];
  const affectedSelectedGroups = notifyContactsNotInGroup
    .flatMap((item) => item.groupsTheNotifyContactIsNotMemberOf)
    .filter((groupId) => selectedSubscriptionGroupIds.includes(groupId));

  return (
    <Popup
      trigger={children}
      open={isPopupOpen}
      position='bottom left'
      className='tw-w-96 tw-p-5 md:tw-w-[400px]'
      flowing
      on='click'
      onOpen={() => setIsPopupOpen(true)}
      onClose={() => setIsPopupOpen(false)}
    >
      <Popup.Header className='tw-flex tw-flex-col'>
        <span>
          Add to Groups{" ("}
          {notifyContactIds.length}
          {")"}
        </span>
      </Popup.Header>

      <Popup.Content className='tw-flex tw-flex-col tw-gap-3'>
        <Form.Field className='tw-m-0'>
          <Dropdown
            options={
              subscriptionGroups
                ?.filter((group) => group.visibility !== "Public")
                ?.map((group) => ({
                  key: group.id,
                  value: group.id,
                  text: group.name,
                })) ?? []
            }
            placeholder='Subscription Group'
            clearable
            loading={isSubscriptionGroupsLoading}
            selectOnBlur={false}
            multiple
            search
            fluid
            selection
            value={selectedSubscriptionGroupIds}
            onChange={(_, { value }) => setSelectedSubscriptionGroupIds(value as number[])}
            renderLabel={(label) => {
              return (
                <Label
                  icon={affectedSelectedGroups.includes(label.value as number) ? "warning sign" : undefined}
                  className='tw-whitespace-nowrap'
                  content={label.text}
                />
              );
            }}
          />
        </Form.Field>
        <Form.Field className='tw-m-0 tw-flex tw-flex-row tw-items-center'>
          <Checkbox
            label='Re-add users who have unsubscribed'
            checked={reAddNonMembers}
            onChange={(_, { checked }) => setReAddNonMembers(!!checked)}
          />
          <Tooltip>
            This will re-add users who have previously unsubscribed from the selected groups. This may not be compliant
            with regulations unless you have a valid confirmation from the subscriber to re-add them.
          </Tooltip>
        </Form.Field>
        <div className='tw-flex tw-justify-end tw-gap-2'>
          <Button className='tw-m-0' secondary content='Cancel' onClick={() => setIsPopupOpen(false)} />
          <Button
            className='tw-m-0'
            primary
            content='Add'
            disabled={selectedSubscriptionGroupIds.length === 0 || isAddNotifyContactsToSubscriptionGroupsLoading}
            loading={isAddNotifyContactsToSubscriptionGroupsLoading}
            onClick={async () => {
              await bulkAddNotifyContactsToSubscriptionGroups({
                notifyContactIds,
                subscriptionGroupIds: selectedSubscriptionGroupIds,
                overwriteExistingPreference: reAddNonMembers,
              });
              onSubmit();
            }}
          />
        </div>
      </Popup.Content>
    </Popup>
  );
}
